import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { FileEdit, Landmark, TrendingUp, AlertCircle, Pencil } from 'lucide-react';
import { ProjectCreationSetup } from './project-creation-setup';
import CategoryManagement from './category-management';
import { BudgetTypeSelector } from '@/components/budget-configuration';
import { useBudgetConfiguration } from '@/hooks/use-budget-configuration';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import type { BudgetTrackingType } from '@/types/budget-configuration.types';
import { ProjectStatusLabels } from '@/components/settings/ProjectStatusLabels';

export default function SettingsProject() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') || 'project-creation';

  const handleTabChange = (value: string) => {
    setSearchParams({ tab: value });
  };

  // ===== BUDGET CATEGORIES STATE =====
  const [categorySearchTerm, setCategorySearchTerm] = useState('');

  // ===== BUDGET CONFIGURATION STATE =====
  const {
    trackingType,
    hasUnsavedChanges: hasBudgetConfigChanges,
    isSaving: isSavingBudgetConfig,
    updateTrackingType,
    saveConfiguration: saveBudgetConfiguration,
    discardChanges: discardBudgetConfigChanges,
  } = useBudgetConfiguration();
  const [showBudgetConfigSuccess, setShowBudgetConfigSuccess] = useState(false);

  // ===== BUDGET CONFIGURATION HANDLERS =====
  const handleBudgetTypeChange = (value: string) => {
    updateTrackingType(value as BudgetTrackingType);
  };

  const handleSaveBudgetConfig = async () => {
    try {
      await saveBudgetConfiguration();
      setShowBudgetConfigSuccess(true);
      toast.success('Budget configuration saved successfully');
      setTimeout(() => setShowBudgetConfigSuccess(false), 3000);
    } catch (error) {
      console.error('Failed to save budget configuration:', error);
      toast.error('Failed to save budget configuration');
    }
  };

  const handleDiscardBudgetConfig = () => {
    discardBudgetConfigChanges();
    setShowBudgetConfigSuccess(false);
    toast.info('Changes discarded');
  };

  return (
    <div className="flex-1 overflow-auto">
      <div className="max-w-7xl mx-auto p-6 space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Project Settings</h1>
          <p className="text-muted-foreground mt-2">
            Configure project-related settings and preferences
          </p>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={handleTabChange} className="space-y-6">
          <div className="border-b">
            <TabsList variant="icon" showArrows>
              <TabsTrigger variant="icon" value="project-creation" showUnderline>
                <FileEdit className="size-5" />
                Project Creation
              </TabsTrigger>
              <TabsTrigger variant="icon" value="budget-settings" showUnderline>
                <Landmark className="size-5" />
                Budget Settings
              </TabsTrigger>
              <TabsTrigger variant="icon" value="status-labels" showUnderline>
                <Pencil className="size-5" />
                Status Labels
              </TabsTrigger>
              <TabsTrigger variant="icon" value="thresholds" showUnderline>
                <TrendingUp className="size-5" />
                Thresholds
              </TabsTrigger>
            </TabsList>
          </div>

          {/* Project Creation Tab */}
          <TabsContent value="project-creation" className="mt-0">
            <ProjectCreationSetup />
          </TabsContent>

          {/* Budget Settings Tab */}
          <TabsContent value="budget-settings" className="mt-0">
            <div className="space-y-6">
              {/* Budget Configuration Section */}
              <Card>
                <CardHeader>
                  <CardTitle>Budget Tracking Configuration</CardTitle>
                  <CardDescription>
                    Configure how budgets are tracked across all projects. This setting applies globally to Resource, Sales, and Cost budgets.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Success Message */}
                  {showBudgetConfigSuccess && (
                    <Alert className="border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-950">
                      <AlertCircle className="h-4 w-4 text-green-600 dark:text-green-400" />
                      <AlertTitle className="text-green-800 dark:text-green-200">Configuration Saved</AlertTitle>
                      <AlertDescription className="text-green-700 dark:text-green-300">
                        Your budget tracking configuration has been updated successfully.
                      </AlertDescription>
                    </Alert>
                  )}

                  {/* Unsaved Changes Warning */}
                  {hasBudgetConfigChanges && (
                    <Alert>
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription>
                        You have unsaved changes. Click "Save Configuration" to apply your changes.
                      </AlertDescription>
                    </Alert>
                  )}

                  {/* Budget Type Selector */}
                  <BudgetTypeSelector value={trackingType} onChange={handleBudgetTypeChange} />

                  {/* Action Buttons */}
                  {hasBudgetConfigChanges && (
                    <div className="flex items-center justify-end gap-3 pt-4 border-t">
                      <Button
                        variant="outline"
                        onClick={handleDiscardBudgetConfig}
                        disabled={isSavingBudgetConfig}
                      >
                        Discard Changes
                      </Button>
                      <Button
                        onClick={handleSaveBudgetConfig}
                        disabled={isSavingBudgetConfig}
                      >
                        {isSavingBudgetConfig ? 'Saving...' : 'Save Configuration'}
                      </Button>
                    </div>
                  )}

                  {/* Info Alert */}
                  <Alert className="border-blue-200 bg-blue-50 dark:border-blue-900 dark:bg-blue-950">
                    <AlertCircle className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                    <AlertDescription className="text-blue-900 dark:text-blue-100">
                      <strong>Global Configuration:</strong> This setting applies to all budget types (Resource, Sales, and Cost) 
                      to ensure consistent tracking and reporting across your projects.
                    </AlertDescription>
                  </Alert>
                </CardContent>
              </Card>

              {/* Budget Categories Section */}
              <div>
                <div className="mb-4">
                  <h2 className="text-2xl font-bold tracking-tight">Budget Categories</h2>
                  <p className="text-muted-foreground mt-1">
                    Manage budget categories used across your projects
                  </p>
                </div>
                <CategoryManagement 
                  searchTerm={categorySearchTerm}
                  setSearchTerm={setCategorySearchTerm}
                />
              </div>
            </div>
          </TabsContent>

          {/* Status Labels Tab */}
          <TabsContent value="status-labels" className="mt-0">
            <ProjectStatusLabels />
          </TabsContent>

          {/* Thresholds Tab */}
          <TabsContent value="thresholds" className="mt-0">
            <Card>
              <CardContent className="pt-6">
                <div className="text-center py-12 space-y-4">
                  <TrendingUp className="h-12 w-12 text-muted-foreground mx-auto" />
                  <div>
                    <h3 className="text-lg font-semibold mb-2">Threshold Settings</h3>
                    <p className="text-sm text-muted-foreground max-w-md mx-auto">
                      Configure project thresholds and limits. This feature will be available soon.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
